import { ChangeEvent, DragEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button } from "..";

interface Props {
  limit?: number;
  message?: string;
  maxSize?: number; // Max file size allowed in MB
  onChange: (files: File[]) => void;
  className?: string;
}

export const ImageDropzone = (props: Props) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  /**
   * Checks that a file has a supported format and file size.
   * @param file
   * @returns
   */
  const validateFile = (file: File) => {
    if (file === undefined) return false;
    if (file.type !== undefined) {
      if (file.type !== "image/jpeg" && file.type !== "image/png") {
        setErrorMessage(t("imagine:fileMustBeJpgOrPng"));
        return false;
      }
    }
    if (file.size !== undefined && props.maxSize !== undefined) {
      if (file.size > props.maxSize * 1000000) {
        setErrorMessage(t("imagine:fileSizeLimitMB", { limit: props.maxSize }));
        return false;
      }
    }
    return true;
  };

  const addImages = (filesArray: File[]) => {
    const newFiles: File[] = [];
    const newPreviews: string[] = [];

    for (const file of filesArray) {
      if (validateFile(file)) {
        newFiles.push(file);
        newPreviews.push(URL.createObjectURL(file));
      }
    }

    setFiles([...files, ...newFiles]);
    setPreviews([...previews, ...newPreviews]);
    props.onChange([...files, ...newFiles]);
  };

  const addDroppedImages = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (props.limit) {
      if (files.length + event.dataTransfer.files.length > props.limit) {
        return;
      }
    }
    console.log("Add images", event.dataTransfer.files.length);
    console.log("Add images", event.dataTransfer.files[0]);

    const filesArray = Array.from(event.dataTransfer.files);
    addImages(filesArray);
  };

  const addSelectedImages = (event: ChangeEvent<HTMLInputElement>) => {
    console.log("Set file", event);
    const filesArray = Array.from(event.target.files || []);
    if (props.limit) {
      if (files.length + filesArray.length > props.limit) {
        return;
      }
    }
    addImages(filesArray);
  };

  const removeImage = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);

    const newPreviews = [...previews];
    newPreviews.splice(index, 1);
    setPreviews(newPreviews);

    props.onChange(newFiles);
  };

  const ignoreDrag = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const openFileExplorer = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const message = props.message || t("imagine:dropImagesHere");

  return (
    <div className={props.className}>
      {errorMessage && (
        <Alert color="danger" className="mb">
          {errorMessage}
        </Alert>
      )}
      <div
        className="dropzone"
        onDrop={addDroppedImages}
        onDragOver={ignoreDrag}
        onDragEnter={ignoreDrag}
      >
        <div>{message}</div>
        <Button
          className="mt"
          onClick={openFileExplorer}
          outline
          disabled={props.limit ? props.limit <= files.length : false}
        >
          {t("imagine:pickFiles")}
        </Button>
      </div>
      <input
        type="file"
        ref={fileInputRef}
        multiple
        className="dropzone-input-file"
        onChange={addSelectedImages}
      />

      <div className="previews">
        {previews.map((preview, index) => (
          <div className="preview" key={index}>
            <img src={preview} alt="preview" />
            <Button
              color="danger"
              icon="cross"
              size="sm"
              iconSize="sm"
              onClick={() => removeImage(index)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
